import {HttpClient, HttpParams} from "@angular/common/http";
import { HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "src/environments/environment";
import { OrganizationCompany } from "./models/organization-company.model";
import { BehaviorSubject, Observable } from "rxjs";
import { OrganizationUser } from "./models/organization-user.model";
import { Modules } from "../enums/modules.enum";
import { OnboardingPermissions } from "../enums/permissions/onboarding-permissions.enum";
import {tap} from 'rxjs/operators';
import {IDepartment} from '../models/departments&services/IDepartment.interface';

@Injectable({
    providedIn: 'root'
})

export class OrganizationsService {
    url = environment.apiConfig.IdentityAPI.endpoint + '/organizations';

    private _companies = new BehaviorSubject<OrganizationCompany[]>([]);

    private headers = new HttpHeaders({
        'permission' : OnboardingPermissions.OnboardingAdmin,
    });

    constructor(private httpClient: HttpClient) {}

    getOrganizationCompanies(): Observable<OrganizationCompany[]> {
        return this.httpClient.get<OrganizationCompany[]>(this.url + '/companies', { headers: this.headers }).pipe(
          tap(companies => this._companies.next(companies))
        );
    }

  getOrganizationDepartments(): Observable<IDepartment[]> {
    return this.httpClient.get<IDepartment[]>(this.url + `/departments`);
  }

    isFirstCompany(): boolean {
      return this._companies.value.length < 1;
    }

    getOrganizationUsers(): Observable<OrganizationUser[]> {
        return this.httpClient.get<OrganizationUser[]>(this.url + '/users');
    }

    getAllOrganizationUsers(): Observable<OrganizationUser[]> {
        return this.httpClient.get<OrganizationUser[]>(this.url + '/usersAll');
    }

    setUserStatus(userId: number, isActive: boolean): Observable<void> {
        return this.httpClient.patch<void>(this.url + `/users/${userId}/status`, { isActive });
    }

    setAdmin(userId: number): Observable<void> {
        return this.httpClient.post<void>(this.url + `/admins/${userId}`, { headers: this.headers });
    }

    removeAdmin(userId: number): Observable<void> {
        return this.httpClient.delete<void>(this.url + `/admins/${userId}`, { headers: this.headers });
    }

  setEmployeeSalary(userId: number, salary: number, effectiveAt?: string | null, fromPayrollSync?: boolean): Observable<void> {
    let params = new HttpParams().set('salary', salary);
    if (effectiveAt) params = params.set('effectiveAt', effectiveAt);
    if (fromPayrollSync) params = params.set('fromPayrollSync', 'true');
    return this.httpClient.patch<void>(this.url + `/users/${userId}/salary`, {}, { params });
  }

  getSalaryHistory(userId: number): Observable<SalaryHistoryEntry[]> {
    return this.httpClient.get<SalaryHistoryEntry[]>(this.url + `/users/${userId}/salary/history`);
  }

  createSalaryHistoryEntry(userId: number, amount: number, effectiveAt: string): Observable<SalaryHistoryEntry> {
    const params = new HttpParams().set('amount', amount).set('effectiveAt', effectiveAt);
    return this.httpClient.post<SalaryHistoryEntry>(this.url + `/users/${userId}/salary/history`, {}, { params });
  }

  updateSalaryHistoryEntry(id: number, amount: number, effectiveAt: string): Observable<SalaryHistoryEntry> {
    const params = new HttpParams().set('amount', amount).set('effectiveAt', effectiveAt);
    return this.httpClient.put<SalaryHistoryEntry>(this.url + `/users/salary/history/${id}`, {}, { params });
  }

  deleteSalaryHistoryEntry(id: number): Observable<void> {
    return this.httpClient.delete<void>(this.url + `/users/salary/history/${id}`);
  }

  setEmployeeWorkingHours(userId: number, workingHours: number): Observable<void> {
    const params = new HttpParams()
      .set('workingHours', workingHours);
    return this.httpClient.patch<void>(this.url + `/users/${userId}/working-hours`, {}, {
      params: params
    });
  }

  setEmployeeBillableHourlyRate(
    userId: number,
    value: number | null,
    mode: 'percentage' | 'flat' | null,
    amount: number | null,
    employerCostHourlyRate: number | null,
    effectiveAt: string | null
  ): Observable<void> {
    let params = new HttpParams();
    if (value != null) params = params.set('value', value);
    if (mode != null) params = params.set('mode', mode);
    if (amount != null) params = params.set('amount', amount);
    if (employerCostHourlyRate != null) params = params.set('employerCostHourlyRate', employerCostHourlyRate);
    if (effectiveAt) params = params.set('effectiveAt', effectiveAt);
    return this.httpClient.patch<void>(this.url + `/users/${userId}/billable-hourly-rate`, {}, {
      params: params
    });
  }

  getEmployeeBillableHourlyRateHistory(userId: number): Observable<BillableHourlyRateHistoryEntry[]> {
    return this.httpClient.get<BillableHourlyRateHistoryEntry[]>(
      this.url + `/users/${userId}/billable-hourly-rate/history`
    );
  }

  updateBillableHourlyRateHistoryEntry(
    id: number,
    value: number | null,
    mode: 'percentage' | 'flat' | null,
    amount: number | null,
    employerCostHourlyRate: number | null,
    effectiveAt: string | null
  ): Observable<BillableHourlyRateHistoryEntry> {
    let params = new HttpParams();
    if (value != null) params = params.set('value', value);
    if (mode != null) params = params.set('mode', mode);
    if (amount != null) params = params.set('amount', amount);
    if (employerCostHourlyRate != null) params = params.set('employerCostHourlyRate', employerCostHourlyRate);
    if (effectiveAt) params = params.set('effectiveAt', effectiveAt);
    return this.httpClient.put<BillableHourlyRateHistoryEntry>(
      this.url + `/users/billable-hourly-rate/history/${id}`, {}, { params }
    );
  }

  deleteBillableHourlyRateHistoryEntry(id: number): Observable<void> {
    return this.httpClient.delete<void>(this.url + `/users/billable-hourly-rate/history/${id}`);
  }

  getUserExtraExpenses(userId: number): Observable<UserExtraExpense[]> {
    return this.httpClient.get<UserExtraExpense[]>(this.url + `/users/${userId}/extra-expenses`);
  }

  createUserExtraExpense(userId: number, dto: UserExtraExpenseDto): Observable<UserExtraExpense> {
    return this.httpClient.post<UserExtraExpense>(this.url + `/users/${userId}/extra-expenses`, dto);
  }

  updateUserExtraExpense(id: number, dto: UserExtraExpenseDto): Observable<UserExtraExpense> {
    return this.httpClient.put<UserExtraExpense>(this.url + `/users/extra-expenses/${id}`, dto);
  }

  deleteUserExtraExpense(id: number): Observable<void> {
    return this.httpClient.delete<void>(this.url + `/users/extra-expenses/${id}`);
  }
}

export interface BillableHourlyRateHistoryEntry {
  id: number;
  value: number | null;
  mode: 'percentage' | 'flat' | null;
  effectiveHourlyRate: number | null;
  employerCostHourlyRate: number | null;
  changedAt: string;
}

export enum UserExtraExpenseFrequency {
  Monthly = 0,
  Yearly = 1
}

export interface UserExtraExpense {
  id: number;
  userId: number;
  name: string;
  amount: number;
  frequency: UserExtraExpenseFrequency;
  notes: string | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  createdAt: string;
  updatedAt: string | null;
}

export interface UserExtraExpenseDto {
  name: string;
  amount: number;
  frequency: UserExtraExpenseFrequency;
  notes: string | null;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
}

export interface SalaryHistoryEntry {
  id: number;
  amount: number;
  changedAt: string;
  fromPayrollSync: boolean;
}
